import { useEffect, useState } from 'react';

/**
 * Three states, not two. A plain light/dark switch takes away the ability to
 * follow the operating system, which is what most people actually want, and
 * what the app did before this existed.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'nhc-theme';

function systemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function storedPreference(): ThemePreference {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    // Private browsing and locked-down profiles throw on access, not on read.
    return 'system';
  }
}

function apply(theme: 'light' | 'dark'): void {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}

export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(storedPreference);

  useEffect(() => {
    apply(preference === 'system' ? systemTheme() : preference);

    try {
      if (preference === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Storage is a convenience here; the theme still applies without it.
    }

    // Only while following the system does an OS change mean anything.
    if (preference !== 'system') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply(systemTheme());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference]);

  return { preference, setPreference };
}
